import { JWTHttpClient } from '@/services/JWTHttpClient'
import { useUserStore } from '@/stores/user'

const SUPPORT_AGENT_URL = import.meta.env.VITE_SUPPORT_AGENT_API_HOST || '/service-agent-stage'
const SUMMARIZER_SERVICE_URL = `${SUPPORT_AGENT_URL}/v1/summarizer`

const agentClient = new JWTHttpClient(import.meta.env.VITE_SUPPORT_AGENT_API_HOST)

function getAgentClient() {
  return agentClient
}

function getSummarizerClient() {
  return new JWTHttpClient(SUPPORT_AGENT_URL)
}

function currentTraceUser(): { user?: { id: string; name: string } } {
  try {
    const data = useUserStore().userData
    if (!data?.id) return {}
    const name =
      data.name ||
      data.full_name ||
      `${data.first_name || ''} ${data.last_name || ''}`.trim()
    return { user: { id: data.id, name } }
  } catch {
    return {}
  }
}

/**
 * Wrapper around JWTHttpClient.get that returns ``fallback`` for 204 No Content
 * responses (the backend returns 204 when no summaries exist yet).
 */
async function summarizerGet<T>(path: string, params: Record<string, string>, fallback: T): Promise<T> {
  try {
    return await getSummarizerClient().get<T>(path, params)
  } catch (err: any) {
    if (err?.message?.includes('204') || err?.message?.includes('JSON')) {
      return fallback
    }
    throw err
  }
}

export async function fetchCustomerSummary(customerId: string) {
  return summarizerGet<any[]>('v1/summarizer/customer', { customerId }, [])
}

export async function fetchContactSummary(contactId: string) {
  return summarizerGet<any[]>('v1/summarizer/contact', { contactId }, [])
}

export interface CaseSummaryDiagnostics {
  path?: string
  comms?: number
  tasks?: number
  journeys?: number
  comms_unreadable?: boolean
  tasks_unreadable?: boolean
  journeys_unreadable?: boolean
  [key: string]: unknown
}

export interface CaseSummary {
  summary: string;
  one_line_summary?: string;
  status: string;
  source: string;
  created_date: string;
  completed_date: string;
  trace_id?: string;
  sentiment?: {
    emotion: string;
    score: number;
    confidence: number;
    rationale?: string;
    key_indicators?: string[];
  };
  issue_category?: string;
  resolution_status?: string;
  next_steps?: string[];
  /** Provenance: what fed the summary and whether any lookup failed. */
  summary_diagnostics?: CaseSummaryDiagnostics;
  case_data: {
    case_metadata: {
      id: string;
      sponsor_partners_teams_id: string;
      owner_partners_teams_id: string;
      source: string;
      collaborator_partners_teams_ids: string[];
      display_name: string;
      idr_isq: string;
      idr_process: any[];
      idr_resolution: any[];
      dicts: string[];
      canned_responses: any[];
      kbs: any[];
      categories: string[];
      type: number;
      escalated: boolean;
      claimed: string;
      created: string;
      customer: {
        id: string;
        name: string | null;
        email: string;
        phone: string | null;
        created: string;
      };
      location: {
        id: string;
        zipcode: string;
        phone: string | null;
        site_name: string;
        created: string;
      };
      customerUser: {
        id: string;
        first_name: string;
        last_name: string | null;
        email: string;
        sms_number: string;
        created: string;
      };
    };
    case_transcript: any[];
    case_id: string;
    org_id: string;
    source: string;
    summary: string | null;
  };
}

export async function fetchCaseSummary(caseId: string): Promise<CaseSummary[]> {
  return summarizerGet<CaseSummary[]>('v1/summarizer/case', { caseId }, [])
}

export async function fetchLocationSummary(locationId: string) {
  return summarizerGet<any[]>('v1/summarizer/location', { locationId }, [])
}

export interface LocationSummary {
  cases: Array<{
    case_metadata: {
      id: string;
      display_name: string;
      idr_isq: string;
      idr_process: any[];
      idr_resolution: any[];
      status: number;
      priority: number;
    };
    case_transcript: any[];
    case_id: string;
    created_at: string;
  }>;
  location_id: string;
  org_id: string;
  start_date: string;
  end_date: string;
  source: string | null;
  summary: string;
  suggestions: string[];
  trending_topics: string[];
  open_issues: string[];
  engagement_level: string;
  engagement_rationale?: string;
  engagement_evidence?: string;
  sentiment: {
    emotion: string;
    score: number;
    confidence: number;
    rationale?: string;
    key_indicators: string[];
  };
  status: string;
  created_date: string;
  completed_date: string;
  trace_id: string;
  most_common_issue?: string;
}

// --- Unified previous-summaries lookup ---

export interface PreviousSummariesResponse {
  summary_type: string
  org_id: string
  entity_id: string | null
  count: number
  summaries: any[]
}

/**
 * Fetch previously saved summaries for an entity via the unified lookup endpoint.
 * Returns null (204 No Content) when no summaries exist for the entity yet.
 */
export async function fetchPreviousSummaries(params: {
  orgId: string
  summaryType: 'case' | 'customer' | 'location' | 'contact' | 'article' | 'comms'
  entityId?: string
  limit?: number
}): Promise<PreviousSummariesResponse | null> {
  const query: Record<string, string> = {
    orgId: params.orgId,
    summaryType: params.summaryType,
  }
  if (params.entityId) query.entityId = params.entityId
  if (params.limit) query.limit = String(params.limit)

  return summarizerGet<PreviousSummariesResponse | null>('v1/summarizer/summaries', query, null)
}

// --- Summarizer Agent (via /agents/run) ---

export type SummaryType = 'case' | 'customer' | 'location' | 'contact' | 'comms' | 'article'

export interface AgentSummaryParams {
  agentId: string
  orgId: string
  summaryType: SummaryType
  query: string
  context: Record<string, any>
}

export interface AgentRunResponse {
  status: 'success' | 'fallback' | 'escalated' | 'error'
  message: string
  sources?: Array<string | { title?: string; url?: string }>
  related_links?: Array<{ title?: string; url?: string }>
  triggers?: { escalated?: boolean; escalation_reason?: string; fallback?: boolean; fallback_reason?: string }
  trace_id?: string
  duration_ms?: number
  model_used?: string
  result_id?: string
  metadata?: Record<string, unknown>
}

/** @deprecated Use AgentRunResponse instead */
export type AgentSummaryResponse = AgentRunResponse

/**
 * Client budget for a summarizer run.
 *
 * Must stay UNDER the server's own request timeout (Cloud Run is configured at
 * 300s — see `*-cloudrun.yaml`), so that a slow-but-successful run still has a
 * chance to reach us before we give up. The previous 120s budget sat well below
 * observed production runs (~220s), so the browser routinely aborted a request
 * the server went on to complete and persist — surfacing as "generation failed"
 * on a summary that had in fact been generated.
 */
export const AGENT_RUN_TIMEOUT_MS = 240000

/**
 * Raised when POST /agents/run exceeds {@link AGENT_RUN_TIMEOUT_MS}.
 *
 * Distinct from a generic failure because the work is probably NOT lost: the
 * `/agents/run` handler has no client-disconnect check, so it keeps running and
 * writes the result to the summary collection regardless of whether we are
 * still listening. Callers should try to recover the persisted summary (see
 * `fetchPreviousSummaries`) before reporting an error.
 */
export class SummarizerTimeoutError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'SummarizerTimeoutError'
  }
}

/**
 * Run the summarizer agent synchronously via POST /agents/run.
 * Uses JWTHttpClient so the Bearer token is included automatically.
 *
 * Throws {@link SummarizerTimeoutError} on timeout so callers can tell
 * "too slow, probably still running" apart from "actually failed".
 */
export async function runSummarizerAgent(params: AgentSummaryParams): Promise<AgentRunResponse> {
  const AGENT_API_BASE = import.meta.env.VITE_SUPPORT_AGENT_API_HOST || '/service-agent-stage'
  // Retries stay off: a timed-out run is still executing server-side, so
  // re-issuing it would duplicate an expensive job rather than rescue it.
  const client = new JWTHttpClient(AGENT_API_BASE, {
    timeoutMs: AGENT_RUN_TIMEOUT_MS,
    maxRetries: 0,
  })

  try {
    return await client.post<AgentRunResponse>(
      'agents/run',
      {
        query: params.query,
        session_id: crypto.randomUUID(),
        context: {
          summary_type: params.summaryType,
          org_id: params.orgId,
          ...currentTraceUser(),
          ...params.context,
        },
      },
      { agent_id: params.agentId }
    )
  } catch (err) {
    // JWTHttpClient converts an aborted fetch into a plain Error whose message
    // is "Request timed out after Nms and N retries [...]"; re-type it so the
    // recovery path doesn't have to string-match further up the stack.
    if (err instanceof Error && /timed out/i.test(err.message)) {
      throw new SummarizerTimeoutError(err.message)
    }
    throw err
  }
}

// OpenAI API Types and Functions
export interface Message {
  senderId: string;
  content: string;
}

export interface OpenAIResponse {
  result: string;
  model_used: string;
  tokens_used?: number;
  trace_id?: string;
  error?: string | null;
  is_fallback?: boolean;
}

export interface SummarizeConversationRequest {
  messages: Message[];
  orgId: string;
}

export interface GenerateReplyRequest {
  messages: Message[];
  orgId: string;
}

export interface ReformatEmailRequest {
  content: string;
  orgId: string;
}

export interface ReformatChatRequest {
  content: string;
  orgId: string;
}

export interface ReformatKnowledgeBaseRequest {
  content: string;
  orgId: string;
}

export interface CleanupEmailRequest {
  content: string;
  orgId: string;
}

export interface GeneralOpenAIRequest {
  model: string;
  messages: Array<{
    role: string;
    content: string;
  }>;
  orgId: string;
  temperature?: number;
  max_tokens?: number;
  top_p?: number;
  frequency_penalty?: number;
  presence_penalty?: number;
  stop?: string | string[];
}

// OpenAI API Functions
export async function summarizeConversation(request: SummarizeConversationRequest): Promise<OpenAIResponse> {
  const response = await fetch(`${SUMMARIZER_SERVICE_URL}/openai/summarize-conversation`, {
    headers: {
      'accept': 'application/json',
      'content-type': 'application/json',
    },
    method: 'POST',
    credentials: 'omit',
    body: JSON.stringify({
      messages: request.messages,
      org_id: request.orgId
    }),
  });
  
  if (!response.ok) {
    // Try to extract error detail from response body
    let errorMessage = 'Failed to summarize conversation';
    try {
      const errorData = await response.json();
      errorMessage = errorData.detail || errorData.message || errorMessage;
    } catch (parseError) {
      // If parsing fails, use generic error message
      console.warn('Failed to parse error response:', parseError);
    }
    throw new Error(errorMessage);
  }
  
  return await response.json();
}

// ---------------------------------------------------------------------------
// Agent-based action mode helpers (uses the deployed summarizer agent)
// ---------------------------------------------------------------------------

/**
 * Run an action mode (cleanup, reformat, generate reply) through the
 * deployed summarizer agent — the same agent used for summaries.
 * Maps the agent response to the legacy OpenAIResponse shape the UI expects.
 */
async function _runAction(
  orgId: string,
  actionMode: string,
  query: string,
  extra: Record<string, any> = {},
): Promise<OpenAIResponse> {
  const agentId = await _getSummarizerAgentId(orgId)
  if (!agentId) throw new Error('No deployed summarizer agent found')

  const client = new JWTHttpClient(SUPPORT_AGENT_URL, { timeoutMs: 60000, maxRetries: 0 })

  const res = await client.post<any>(
    'agents/run',
    {
      query,
      session_id: crypto.randomUUID(),
      context: { action_mode: actionMode, ...currentTraceUser(), ...extra },
    },
    { agent_id: agentId, org_id: orgId },
  )

  return {
    result: res?.message ?? res?.response ?? '',
    model_used: res?.model_used ?? '',
    tokens_used: res?.response_tokens,
    trace_id: res?.trace_id ?? null,
    error: res?.error ?? null,
    is_fallback: res?.is_fallback ?? false,
  }
}

/** Reuse the summarizer agent lookup from useSummarizerAgent. */
let _cachedSummarizerAgentId: string | null = null
let _cachedSummarizerOrgId: string | null = null

async function _getSummarizerAgentId(orgId: string): Promise<string | null> {
  if (_cachedSummarizerAgentId && _cachedSummarizerOrgId === orgId) return _cachedSummarizerAgentId

  const client = new JWTHttpClient(SUPPORT_AGENT_URL)
  const res = await client.get<{ agents: any[] }>('agents/list', { org_id: orgId })
  const agents: any[] = res?.agents ?? []

  const deployed = agents.find(
    (a: any) =>
      a.agent_type === 'summarizer' &&
      (a.status === 'deployed' || a.deployment_status === 'deployed')
  )

  const agentId = deployed?.agent_id || deployed?.id || null
  _cachedSummarizerAgentId = agentId
  _cachedSummarizerOrgId = orgId
  return agentId
}

// ---------------------------------------------------------------------------
// Public action functions (match old signatures for drop-in replacement)
// ---------------------------------------------------------------------------

export async function generateReply(request: GenerateReplyRequest): Promise<OpenAIResponse> {
  return _runAction(request.orgId, 'generate_reply', '', {
    messages: request.messages,
    channel: 'email',
  })
}

export async function reformatEmail(request: ReformatEmailRequest): Promise<OpenAIResponse> {
  return _runAction(request.orgId, 'reformat_reply', request.content, {
    channel: 'email',
  })
}

export async function reformatChat(request: ReformatChatRequest): Promise<OpenAIResponse> {
  return _runAction(request.orgId, 'reformat_reply', request.content, {
    channel: 'chat',
  })
}

export async function reformatKnowledgeBase(request: ReformatKnowledgeBaseRequest): Promise<OpenAIResponse> {
  return _runAction(request.orgId, 'reformat_reply', request.content, {
    channel: 'email',
  })
}

export async function cleanupEmail(request: CleanupEmailRequest): Promise<OpenAIResponse> {
  return _runAction(request.orgId, 'cleanup_reply', request.content, {
    channel: 'email',
  })
}

export async function chatCompletion(request: GeneralOpenAIRequest): Promise<OpenAIResponse> {
  const response = await fetch(`${SUMMARIZER_SERVICE_URL}/openai/chat-completion`, {
    headers: {
      'accept': 'application/json',
      'content-type': 'application/json',
    },
    method: 'POST',
    credentials: 'omit',
    body: JSON.stringify({
      model: request.model,
      messages: request.messages,
      org_id: request.orgId,
      ...(request.temperature !== undefined && { temperature: request.temperature }),
      ...(request.max_tokens !== undefined && { max_tokens: request.max_tokens }),
      ...(request.top_p !== undefined && { top_p: request.top_p }),
      ...(request.frequency_penalty !== undefined && { frequency_penalty: request.frequency_penalty }),
      ...(request.presence_penalty !== undefined && { presence_penalty: request.presence_penalty }),
      ...(request.stop !== undefined && { stop: request.stop })
    }),
  });
  
  if (!response.ok) {
    throw new Error('Failed to complete chat request');
  }
  
  return await response.json();
}

export interface PassthroughOpenAIRequest {
  orgId: string;
  [key: string]: any;
}

export async function passthroughOpenAI(request: PassthroughOpenAIRequest): Promise<any> {
  const { orgId, ...rest } = request;
  const response = await fetch(`${SUMMARIZER_SERVICE_URL}/openai/passthrough`, {
    headers: {
      'accept': 'application/json',
      'content-type': 'application/json',
    },
    method: 'POST',
    credentials: 'omit',
    body: JSON.stringify({
      ...rest,
      org_id: orgId
    }),
  });
  
  if (!response.ok) {
    throw new Error('Failed to process OpenAI passthrough request');
  }
  
  return await response.json();
}

// Analytics API Types and Functions
export interface TraceStats {
  trace_name: string | null;
  total_runs: number;
  successful_runs: number;
  failed_runs: number;
  avg_duration_ms: number | null;
  total_tokens: number | null;
  avg_tokens_per_run: number | null;
}

export interface OrgTracesAnalytics {
  org_id: string;
  date_range_start: string;
  date_range_end: string;
  total_traces: number;
  trace_stats: TraceStats[];
}

export async function fetchOrgTracesAnalytics(
  orgId: string, 
  daysBack?: number, 
  startDate?: string, 
  endDate?: string
): Promise<OrgTracesAnalytics> {
  const client = getAgentClient()
  
  // Build query parameters
  const params: Record<string, string> = {}
  
  if (startDate && endDate) {
    // Use custom date range
    params.start_date = startDate
    params.end_date = endDate
  } else if (daysBack !== undefined) {
    // Use days back
    params.days_back = daysBack.toString()
  } else {
    // Default to 1 day
    params.days_back = '1'
  }
  
  return client.get<OrgTracesAnalytics>(
    `/api/analytics/org/${encodeURIComponent(orgId)}/traces`,
    params
  )
}

export async function fetchOrgTraceNames(orgId: string, daysBack: number = 1): Promise<string[]> {
  const client = getAgentClient()
  
  return client.get<string[]>(
    `/api/analytics/org/${encodeURIComponent(orgId)}/trace-names`,
    { days_back: daysBack.toString() }
  )
}

export interface TraceListItem {
  trace_id: string;
  trace_name: string;
  timestamp: string;
  user_id: string;
  session_id: string | null;
  input_preview: string;
  output_preview: string;
  total_cost: number;
  latency_ms: number | null;
  tags: string[];
  level?: string | null;
  has_error?: boolean;
  status_message?: string | null;
}

export interface TraceListResponse {
  org_id: string;
  trace_name: string;
  total_count: number;
  traces: TraceListItem[];
  next_cursor: string | null;
}

export interface TraceListFilters {
  daysBack?: number;
  startDate?: string;
  endDate?: string;
  scoreName?: string;
  scoreOperator?: string;
  scoreValue?: number;
  tags?: string[];
  hasError?: boolean;
  userId?: string;
  status?: 'needs review' | 'reviewed' | 'ignored';
  needsReview?: boolean;
}

export async function fetchTraceList(
  orgId: string,
  traceName: string,
  limit: number = 50,
  cursor?: string,
  filters?: TraceListFilters
): Promise<TraceListResponse> {
  const client = getAgentClient()
  
  const params: Record<string, string> = {
    limit: limit.toString(),
  }
  
  // Date filtering
  if (filters?.startDate) {
    params.start_date = filters.startDate
  } else if (filters?.daysBack) {
    params.days_back = filters.daysBack.toString()
  } else {
    // Default to 7 days
    params.days_back = '7'
  }
  
  if (filters?.endDate) {
    params.end_date = filters.endDate
  }
  
  // Score filtering (all three required)
  if (filters?.scoreName && filters?.scoreOperator && filters?.scoreValue !== undefined) {
    params.score_name = filters.scoreName
    params.score_operator = filters.scoreOperator
    params.score_value = filters.scoreValue.toString()
  }
  
  // Tags filtering
  if (filters?.tags && filters.tags.length > 0) {
    params.tags = filters.tags.join(',')
  }
  
  // Error status filtering
  if (filters?.hasError !== undefined) {
    params.has_error = filters.hasError.toString()
  }
  
  // User filtering
  if (filters?.userId) {
    params.user_id = filters.userId
  }
  
  // Status filtering
  if (filters?.status) {
    params.status = filters.status
  }
  
  // Needs review filtering
  if (filters?.needsReview !== undefined) {
    params.needs_review = filters.needsReview.toString()
  }
  
  if (cursor) {
    params.cursor = cursor
  }
  
  return client.get<TraceListResponse>(
    `/api/analytics/org/${encodeURIComponent(orgId)}/traces/${encodeURIComponent(traceName)}/list`,
    params
  )
}

export interface TraceObservation {
  id: string;
  traceId?: string | null;
  type: string;
  name: string | null;
  startTime?: string | null;
  endTime?: string | null;
  completionStartTime?: string | null;
  // model?: string | null; // Will be addressed in https://boomtown.atlassian.net/browse/MS-4995
  modelParameters?: Record<string, any> | null;
  input: any;
  output: any;
  metadata: Record<string, any> | null;
  level?: string | null;
  parentObservationId?: string | null;
  latency?: number | null;
  children?: TraceObservation[];
}

export interface TraceDetail {
  trace_id: string;
  trace_name: string;
  timestamp: string;
  user_id: string;
  session_id: string | null;
  input: any;
  output: any;
  metadata: Record<string, any>;
  tags: string[];
  total_cost: number;
  latency_ms: number | null;
  observations: TraceObservation[];
  level?: string | null;
  has_error?: boolean;
  status_message?: string | null;
}

export async function fetchTraceDetail(
  orgId: string,
  traceName: string,
  traceId: string
): Promise<TraceDetail> {
  const client = getAgentClient()
  
  return client.get<TraceDetail>(
    `/api/analytics/org/${encodeURIComponent(orgId)}/traces/${encodeURIComponent(traceName)}/${encodeURIComponent(traceId)}`
  )
}

// Scoring API Types and Functions
export type ScoreDataType = 'NUMERIC' | 'BOOLEAN' | 'CATEGORICAL';

export interface Score {
  id: string;
  name: string;
  value: number | null;
  data_type: ScoreDataType;
  timestamp: string;
  comment: string | null;
  source: string;
  author_user_id: string | null;
}

export interface TraceScoresResponse {
  trace_id: string;
  scores: Score[];
  score_count: number;
}

export interface SubmitNumericScoreRequest {
  trace_id: string;
  name: string;
  value: number;
  comment?: string;
  user_id: string;
}

export interface SubmitBooleanScoreRequest {
  trace_id: string;
  name: string;
  value: boolean;
  comment?: string;
  user_id: string;
}

export interface SubmitCommentRequest {
  trace_id: string;
  name: string;
  comment: string;
  user_id: string;
}

export interface SubmitScoreResponse {
  success: boolean;
  score_id: string;
  message: string;
}

export interface AggregatedScore {
  score_name: string;
  data_type: ScoreDataType;
  total_count: number;
  avg_value?: number;
  value_distribution?: Record<string, number>;
}

export async function fetchTraceScores(traceId: string): Promise<TraceScoresResponse> {
  const client = getAgentClient()
  
  return client.get<TraceScoresResponse>(
    `/api/analytics/traces/${encodeURIComponent(traceId)}/scores`
  )
}

export async function submitNumericScore(request: SubmitNumericScoreRequest): Promise<SubmitScoreResponse> {
  const client = getAgentClient()
  
  return client.post<SubmitScoreResponse>('/api/analytics/scores/numeric', request)
}

export async function submitBooleanScore(request: SubmitBooleanScoreRequest): Promise<SubmitScoreResponse> {
  const client = getAgentClient()
  
  return client.post<SubmitScoreResponse>('/api/analytics/scores/boolean', request)
}

export async function submitComment(request: SubmitCommentRequest): Promise<SubmitScoreResponse> {
  const client = getAgentClient()
  
  return client.post<SubmitScoreResponse>('/api/analytics/scores/comment', request)
}

export async function fetchAggregatedScores(
  orgId: string,
  daysBack: number = 7,
  traceName?: string
): Promise<AggregatedScore[]> {
  const client = getAgentClient()
  
  const params: Record<string, string> = {
    days_back: daysBack.toString(),
  }
  
  if (traceName) {
    params.trace_name = traceName
  }
  
  return client.get<AggregatedScore[]>(
    `/api/analytics/org/${encodeURIComponent(orgId)}/scores/aggregated`,
    params
  )
}

export interface DeleteTraceResponse {
  success: boolean;
  trace_id: string;
  message: string;
}

export interface BatchDeleteTraceRequest {
  trace_ids: string[];
}

export interface BatchDeleteTraceResponse {
  success: boolean;
  deleted_count: number;
  trace_ids: string[];
  message: string;
}

export async function deleteTrace(traceId: string): Promise<DeleteTraceResponse> {
  const client = getAgentClient()
  
  try {
    return await client.delete<DeleteTraceResponse>(
      `/api/analytics/traces/${encodeURIComponent(traceId)}`
    )
  } catch (error: any) {
    // Handle specific HTTP status codes
    if (error.message?.includes('404')) {
      throw new Error('Trace not found')
    }
    if (error.message?.includes('503')) {
      throw new Error('Langfuse not configured')
    }
    throw new Error('Failed to delete trace')
  }
}

export async function deleteTracesBatch(traceIds: string[]): Promise<BatchDeleteTraceResponse> {
  const client = getAgentClient()
  
  // Note: HttpClient delete method doesn't support request body,
  // so we use fetch directly for this non-standard DELETE with body
  const baseUrl = import.meta.env.VITE_SUPPORT_AGENT_API_HOST || '/service-agent-stage'
  
  try {
    const response = await fetch(`${baseUrl}/api/analytics/traces`, {
      headers: {
        'accept': 'application/json',
        'content-type': 'application/json',
      },
      method: 'DELETE',
      credentials: 'omit',
      body: JSON.stringify({ trace_ids: traceIds }),
    })
    
    if (!response.ok) {
      if (response.status === 400) {
        throw new Error('Invalid request - empty trace_ids list')
      }
      if (response.status === 503) {
        throw new Error('Langfuse not configured')
      }
      throw new Error('Failed to delete traces')
    }
    
    return await response.json()
  } catch (error: any) {
    // Re-throw with appropriate error message
    throw error
  }
}

export async function updateTraceReviewStatus(traceId: string, status: 'reviewed' | 'ignored'): Promise<void> {
  const client = getAgentClient()
  
  await client.patch<void>(
    `/api/analytics/traces/${traceId}/review-status`,
    undefined,
    { status }
  )
} 
